// Single-Keyword Ad Groups (SKAG) — Keywords section, deterministic check.
// Surfaces ad groups with exactly one keyword, sorted to the top of the table. Multi-keyword
// ad groups are shown beneath for context, sorted by keyword count descending.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface AdGroupSummary {
  adGroupId?: string;
  adGroupName?: string;
  campaignName?: string;
  biddingStrategy?: string;
  keywordCount: number;
  singleKeywordText?: string;
  singleKeywordMatchType?: string;
}

interface SkagShape {
  totalAdGroups: number;
  skagCount: number;
  multiKeywordCount: number;
  skagPct: number;
  adGroups: AdGroupSummary[];
}

const STRATEGY_LABEL: Record<string, string> = {
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  MAXIMIZE_CONVERSIONS: 'Max Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Max Conv. Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Imp. Share',
  TARGET_SPEND: 'Target Spend',
  PERCENT_CPC: 'Percent CPC'
};

const MATCH_TYPE_LABEL: Record<string, string> = {
  EXACT: '[exact]',
  PHRASE: '"phrase"',
  BROAD: 'broad'
};

function strategyLabel(s: string | undefined): string {
  if (!s) return '—';
  return STRATEGY_LABEL[s] ?? s;
}

function matchTypeLabel(m: string | undefined): string {
  if (!m) return '';
  return MATCH_TYPE_LABEL[m] ?? m;
}

export const SingleKeywordAdGroupsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as SkagShape;
  const multiKwPct = Math.round((1 - e.skagPct) * 100);

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Ad-group keyword density
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalAdGroups} enabled ad groups
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.skagCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>SKAGs (1 keyword)</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.multiKeywordCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Multi-keyword ad groups</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{multiKwPct}%</div>
            <div className='audience-coverage__lbl'>Multi-keyword share</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-ad-group breakdown
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalAdGroups} ad groups
          </span>
        </h4>
        <table className='evidence__table evidence__table--skag'>
          <thead>
            <tr>
              <th>Ad group</th>
              <th>Campaign</th>
              <th>Bidding strategy</th>
              <th className='evidence__th--right'>Keywords</th>
              <th>SKAG detail</th>
            </tr>
          </thead>
          <tbody>
            {e.adGroups.map(g => {
              const isSkag = g.keywordCount === 1;
              return (
                <tr
                  key={g.adGroupId ?? g.adGroupName}
                  className={classNames({ 'evidence__row--flagged': isSkag })}
                >
                  <td>{g.adGroupName ?? '(unnamed)'}</td>
                  <td>{g.campaignName ?? '—'}</td>
                  <td>{strategyLabel(g.biddingStrategy)}</td>
                  <td className='evidence__td--right'>
                    {isSkag ? <strong>1</strong> : g.keywordCount}
                  </td>
                  <td>
                    {isSkag && g.singleKeywordText ? (
                      <>
                        <span className='evidence__audience-chip'>{g.singleKeywordText}</span>
                        {g.singleKeywordMatchType && (
                          <span className='evidence__inline-note'>
                            {matchTypeLabel(g.singleKeywordMatchType)}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className='evidence__inline-note'>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        SKAG = Single-Keyword Ad Group. Once the dominant pattern under Manual CPC (one keyword per
        ad group, each match type in its own SKAG), now largely deprecated under Smart Bidding:
        themed ad groups with 10-25 closely-related keywords give the bidder more signal and reduce
        fragmentation across the account.
      </p>
    </div>
  );
};
